import { useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

/**
 * shadcn Calendar wrapped in a Popover, styled to match SmartERP's editorial
 * look (mono labels, brutalist borders). `value` is an ISO YYYY-MM-DD string
 * or empty string; `onChange` receives the same shape.
 */
export default function DatePicker({
  value,
  onChange,
  label,
  placeholder = "Select date",
  clearable = true,
  testId,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const shown = selected ? format(selected, "d MMM yyyy") : "";

  return (
    <div className={cn("inline-flex flex-col", className)}>
      {label && <label className="label-sm text-zinc-500">{label}</label>}
      <div className="relative inline-flex items-center">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testId}
              disabled={disabled}
              className={cn(
                "flex items-center justify-between gap-2 min-w-[140px]",
                "border-b border-zinc-300 bg-transparent py-1 text-sm text-left",
                "hover:border-zinc-950 focus:border-zinc-950 outline-none transition-colors",
                clearable && shown ? "pr-7" : "",
                !shown && "text-zinc-400",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <span className="tnum">{shown || placeholder}</span>
              <CalendarBlank size={14} className="text-zinc-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => {
                onChange(d ? format(d, "yyyy-MM-dd") : "");
                setOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {clearable && shown && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={testId ? `${testId}-clear` : undefined}
            aria-label="Clear date"
            className="absolute right-4 text-zinc-400 hover:text-[#FF3E00]"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
